import { ROLE_LABELS_AR, type Role } from '../features/committee/application/roleLabels';
import type { CommitteeMember } from '../features/committee/application/ports/rolesPort';
import { Alert, Badge, Button, Card } from '../shared/components';

import styles from './ScreenLayout.module.css';
import rolesStyles from './RolesPage.module.css';

/**
 * Who on the committee may do what.
 *
 * Until this screen existed, the only account with any role was the one the
 * bootstrap script made, so every approval and every correction in a school of
 * 872 people went through one person.
 */

export interface RolesPageProps {
  readonly members: readonly CommitteeMember[];
  readonly assignableRoles: readonly Role[];
  readonly ownUid: string;
  readonly busyUid: string | null;
  readonly errorAr: string | null;
  readonly noticeAr: string | null;
  readonly onToggleRole: (member: CommitteeMember, role: Role) => void;
}

export function RolesPage({
  members,
  assignableRoles,
  ownUid,
  busyUid,
  errorAr,
  noticeAr,
  onToggleRole,
}: RolesPageProps) {
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.heading}>صلاحيات اللجنة</h1>
          <p className={styles.lede}>
            من يراجع الطلبات، ومن يصحّح السجلات، ومن يضبط المراحل.
          </p>
        </div>
      </div>

      {errorAr ? <Alert severity="error">{errorAr}</Alert> : null}
      {noticeAr ? <Alert severity="success">{noticeAr}</Alert> : null}

      {/* A changed role reaches the person on their next token, not instantly:
          saying so prevents "I gave them access and it did not work". */}
      <Alert severity="info">
        الصلاحية الجديدة تسري بعد أن يسجّل صاحبها خروجًا ودخولًا.
      </Alert>

      {members.length === 0 ? (
        <Card>
          <p className={styles.body}>لا توجد حسابات مرتبطة بعد.</p>
        </Card>
      ) : (
        members.map((member) => (
          <Card
            key={member.authUid}
            title={member.fullName}
            description={member.email}
          >
            <div className={rolesStyles.roles}>
              {assignableRoles.map((role) => {
                const held = member.roles.includes(role);
                /*
                 * A manager cannot drop their own manager role.
                 *
                 * This screen requires that role to open, so demoting yourself
                 * locks the school out of its own permissions — recoverable
                 * only by a script that refuses to run once a manager exists.
                 */
                const wouldLockOut =
                  member.authUid === ownUid && role === 'committee_manager' && held;

                return (
                  <div key={role} className={rolesStyles.role}>
                    <div className={rolesStyles.roleText}>
                      <span className={rolesStyles.roleName}>{ROLE_LABELS_AR[role].name}</span>
                      <span className={rolesStyles.roleWhat}>{ROLE_LABELS_AR[role].what}</span>
                    </div>

                    <div className={rolesStyles.roleActions}>
                      <Badge tone={held ? 'success' : 'neutral'} withDot>
                        {held ? 'ممنوحة' : 'غير ممنوحة'}
                      </Badge>

                      <Button
                        type="button"
                        variant={held ? 'ghost' : 'secondary'}
                        size="sm"
                        loading={busyUid === member.authUid}
                        disabled={wouldLockOut}
                        onClick={() => {
                          onToggleRole(member, role);
                        }}
                      >
                        {held ? 'سحب' : 'منح'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {member.authUid === ownUid ? (
              <p className={rolesStyles.selfNote}>
                هذا حسابك. لا يمكنك سحب صلاحية الإدارة من نفسك.
              </p>
            ) : null}
          </Card>
        ))
      )}
    </div>
  );
}
